// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * The viewer-side half of rack dimensioning. It owns two things that change independently:
 *
 * - **What has been measured.** Re-run whenever React publishes a new set of elements, and cached, so
 *   toggling dimensions on and off redraws without measuring again.
 * - **What is currently drawn.** Segment meshes in an overlay scene, relaid out on every camera change so
 *   they keep a stable on-screen width, plus label positions projected to pixels for React to render.
 *
 * React owns the label HTML and what to measure; everything else lives here.
 */

import type { DimensioningStore } from 'components/Autodesk/extensions/DimensioningExtension/createDimensioningStore';
import {
  projectWorldToContainer,
  updateSegmentMesh,
} from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/helpers';
import type { MeasuredCrossSection } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/buildMeasuredCrossSection';
import { measureRacks } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/measureRacks';
import {
  buildRackDimensionPlan,
  type RackDimensionPlan,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/rendering/buildRackDimensionPlan';
import type { RenderedRackLabelsStore } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/rendering/createRenderedRackLabelsStore';
import { addViewerListener, monitorStoreState } from 'components/Autodesk/extensions/utils';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { THREE } from 'components/Autodesk/viewerThree';
import { isNotNil } from 'helpers/isNotNil';
import { labelTheming } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/labelTheming';

const RACK_DIMENSION_SCENE_NAME = 'rack-dimensions';
const RACK_DIMENSION_COLOR = labelTheming.colors.black;
/** Dimension lines are thin in world units, so keep them at least this wide on screen. */
const RACK_DIMENSION_MIN_PIXEL_WIDTH = 2;

const EMPTY_PLAN: RackDimensionPlan = { segments: [], labels: [] };

export class RackDimensionRenderingManager {
  private readonly viewer: Autodesk.Viewing.GuiViewer3D;
  private readonly dimensioningStore: DimensioningStore;
  private readonly renderedRackLabelsStore: RenderedRackLabelsStore;

  private readonly material: THREE.MeshBasicMaterial;
  /** Unit cylinder shared by every segment mesh, so nothing is recreated as the camera moves. */
  private readonly geometry: THREE.CylinderGeometry;
  /** Reused across plans — a mesh beyond the current plan's segment count is hidden, not destroyed. */
  private segmentMeshes: THREE.Mesh[] = [];

  private plan: RackDimensionPlan = EMPTY_PLAN;
  /** What the current selection measured to, so toggling dimensions redraws without re-measuring. */
  private measuredCrossSections: MeasuredCrossSection[] = [];
  /** Identifies the newest measurement, so a slower earlier one can't overwrite it when it resolves. */
  private latestMeasurementId = 0;

  private cleanUpFns: (() => void)[] = [];
  /** `true` once `dispose()` has run; every method no-ops afterwards. */
  private disposed = false;

  constructor(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dimensioningStore: DimensioningStore,
    renderedRackLabelsStore: RenderedRackLabelsStore,
  ) {
    this.viewer = viewer;
    this.dimensioningStore = dimensioningStore;
    this.renderedRackLabelsStore = renderedRackLabelsStore;

    this.drawMeasuredRacks = this.drawMeasuredRacks.bind(this);
    this.measureSelection = this.measureSelection.bind(this);
    this.ensureOverlayScene = this.ensureOverlayScene.bind(this);
    this.onCameraChanged = this.onCameraChanged.bind(this);
    this.clearDimensions = this.clearDimensions.bind(this);

    this.material = new THREE.MeshBasicMaterial({
      color: RACK_DIMENSION_COLOR,
      depthTest: false,
      depthWrite: false,
    });
    this.geometry = new THREE.CylinderGeometry(1, 1, 1, 4, 1, false);

    this.cleanUpFns.push(
      addViewerListener(viewer, Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.onCameraChanged),
      addViewerListener(viewer, Autodesk.Viewing.MODEL_UNLOADED_EVENT, this.clearDimensions),
      /** On changes to the visibility of rack dimensions, draw or clear them. */
      monitorStoreState(dimensioningStore, ({ dimensionsVisible }) => dimensionsVisible, this.drawMeasuredRacks),
      /** On changes to the elements eligible for dimensioning, re-measure. */
      monitorStoreState(dimensioningStore, ({ dimensionableDbIds }) => dimensionableDbIds, this.measureSelection),
    );

    if (isNotNil(this.viewer.model)) {
      this.ensureOverlayScene();
    } else {
      this.cleanUpFns.push(
        addViewerListener(viewer, Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT, this.ensureOverlayScene),
      );
    }
  }

  private get rackDimensionsVisible(): boolean {
    return this.dimensioningStore.getSnapshot().dimensionsVisible;
  }

  private ensureOverlayScene() {
    const { impl } = this.viewer;
    if (!impl.overlayScenes || !(RACK_DIMENSION_SCENE_NAME in impl.overlayScenes)) {
      impl.createOverlayScene(RACK_DIMENSION_SCENE_NAME);
    }
  }

  /** Segment widths and label positions are both screen-relative, so both follow the camera. */
  private onCameraChanged() {
    if (this.disposed) return;
    this.renderPlan();
  }

  private createSegmentMesh(): THREE.Mesh {
    this.ensureOverlayScene();
    const mesh = new THREE.Mesh(this.geometry, this.material);
    mesh.visible = false;
    this.viewer.impl.addOverlay(RACK_DIMENSION_SCENE_NAME, mesh);
    this.segmentMeshes.push(mesh);
    return mesh;
  }

  /** Lay out the current plan's segment meshes and publish its labels' pixel positions. */
  private renderPlan() {
    // The camera drives this on every frame and dimensions are off by default, so with nothing drawn and
    // nothing to draw, skip the label publish and the viewer invalidation.
    const isEmptyPlan = this.plan.segments.length === 0 && this.plan.labels.length === 0;
    if (isEmptyPlan && this.segmentMeshes.every((mesh) => !mesh.visible)) return;

    this.plan.segments.forEach((segment, index) => {
      const mesh = this.segmentMeshes[index] ?? this.createSegmentMesh();
      updateSegmentMesh(
        mesh,
        segment.start,
        segment.end,
        segment.radiusFt,
        this.viewer,
        RACK_DIMENSION_MIN_PIXEL_WIDTH,
      );
    });
    this.segmentMeshes.slice(this.plan.segments.length).forEach((mesh) => {
      // Meshes are made visible/invisible by directly mutating them
      // oxlint-disable-next-line evolve/no-param-reassign
      mesh.visible = false;
    });

    this.renderedRackLabelsStore.setLabels(
      this.plan.labels.map(({ rackDimensionLabelId, text, anchor }) => {
        const { x, y, visible } = projectWorldToContainer(this.viewer, anchor);
        return { rackDimensionLabelId, text, x, y, visible };
      }),
    );

    this.viewer.impl.invalidate(true, true, true);
  }

  private setPlan(plan: RackDimensionPlan) {
    this.plan = plan;
    this.renderPlan();
  }

  /**
   * Measure the selected spools, publish whether they can be dimensioned, and draw the result when the
   * user has dimensions switched on. The measurement runs even while the toggle is off, because whether a
   * rack is dimensionable decides what the UI offers. A measurement that resolves after a newer one
   * started is discarded, so this is safe to call on every selection change.
   */
  private async measureSelection(dimensionableDbIds: Set<AutodeskDbId>) {
    const measurementId = this.latestMeasurementId + 1;
    this.latestMeasurementId = measurementId;

    if (dimensionableDbIds.size === 0) {
      this.measuredCrossSections = [];
      this.dimensioningStore.setAutoDimensioningStatus('noSelection');
      this.setPlan(EMPTY_PLAN);
      return;
    }

    this.dimensioningStore.setAutoDimensioningStatus('measuring');
    try {
      const crossSections = await measureRacks(this.viewer, dimensionableDbIds);
      if (this.disposed || measurementId !== this.latestMeasurementId) return;
      this.measuredCrossSections = crossSections;
      this.dimensioningStore.setAutoDimensioningStatus(crossSections.length > 0 ? 'supported' : 'unsupported');
      this.drawMeasuredRacks();
    } catch (e) {
      if (this.disposed || measurementId !== this.latestMeasurementId) return;
      // Dimensions are a read-only overlay, so a failed measurement should leave the model usable.
      // oxlint-disable-next-line no-console
      console.error('Could not measure conduit rack dimensions.', e);
      this.measuredCrossSections = [];
      this.dimensioningStore.setAutoDimensioningStatus('unsupported');
      this.setPlan(EMPTY_PLAN);
    }
  }

  /** Draw what the current selection measured to, or nothing while the user has them switched off. */
  private drawMeasuredRacks() {
    this.setPlan(this.rackDimensionsVisible ? buildRackDimensionPlan(this.measuredCrossSections) : EMPTY_PLAN);
  }

  /** Remove all drawn dimensions, e.g. when the model is unloaded. */
  public async clearDimensions() {
    if (this.disposed) return;
    await this.measureSelection(new Set());
  }

  /** Tears down all viewer resources. `disposed` is set last so this method's own cleanup can run. */
  dispose() {
    if (this.disposed) return;
    this.cleanUpFns.forEach((fn) => fn());
    this.cleanUpFns = [];
    this.segmentMeshes = [];
    this.renderedRackLabelsStore.setLabels([]);
    this.material.dispose();
    this.geometry.dispose();
    this.viewer.impl.clearOverlay(RACK_DIMENSION_SCENE_NAME);
    this.disposed = true;
  }
}
